import { Link } from 'react-router-dom';
import { HOME } from '../content/siteContent';
import Seo, { SITE } from './Seo';

const resolveSrc = (src) => {
    if (!src) return src;
    if (/^(https?:)?\/\//i.test(src) || src.startsWith('data:')) return src;
    const base = process.env.PUBLIC_URL || '';
    return src.startsWith('/') ? base + src : `${base}/${src}`;
};

const SOCIAL_ICONS = {
    instagram: (
        <>
            <rect x="3" y="3" width="18" height="18" rx="5" />
            <circle cx="12" cy="12" r="4" />
            <circle cx="17.4" cy="6.6" r="1" fill="currentColor" stroke="none" />
        </>
    ),
    linkedin: (
        <>
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M7 10v7" />
            <circle cx="7" cy="6.8" r="1" fill="currentColor" stroke="none" />
            <path d="M11 17v-4a2.5 2.5 0 0 1 5 0v4" />
        </>
    ),
    spotify: (
        <>
            <circle cx="12" cy="12" r="9" />
            <path d="M7.4 9.4c3-.9 6.3-.6 8.9 1" />
            <path d="M7.8 12.3c2.4-.7 5-.4 7.1.9" />
            <path d="M8.4 15.1c1.9-.5 3.9-.3 5.6.7" />
        </>
    ),
    github: (
        <path d="M9 19c-4 1.2-4-2.2-5.5-2.6M15 21v-3.3a2.9 2.9 0 0 0-.8-2.2c2.7-.3 5.5-1.3 5.5-6a4.6 4.6 0 0 0-1.3-3.2 4.3 4.3 0 0 0-.1-3.2s-1-.3-3.4 1.2a11.6 11.6 0 0 0-6 0C6 2.5 5 2.8 5 2.8a4.3 4.3 0 0 0-.1 3.2A4.6 4.6 0 0 0 3.5 9.2c0 4.6 2.8 5.7 5.5 6a2.9 2.9 0 0 0-.8 2.1V21" />
    ),
    email: (
        <>
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="M4 7l8 6 8-6" />
        </>
    ),
};

function SocialLinks({ items }) {
    if (!items?.length) return null;
    return (
        <div className="home-socials" aria-label="Social links">
            {items.map((s) => (
                <a
                    key={s.href}
                    className="home-social"
                    href={s.href}
                    aria-label={s.label}
                    title={s.label}
                    {...(s.icon === 'email' ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
                >
                    <svg viewBox="0 0 24 24" width="19" height="19" fill="none"
                         stroke="currentColor" strokeWidth="1.5"
                         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        {SOCIAL_ICONS[s.icon]}
                    </svg>
                </a>
            ))}
        </div>
    );
}

function Frame({ img, className }) {
    return (
        <div className={`home-frame ${className || ''}`}>
            {img?.src
                ? <img key={img.src} src={resolveSrc(img.src)} alt={img.alt || ''}
                       width="800" height="1000" loading="eager" decoding="async"/>
                : (
                    <span className="home-frame-ph">
                        <svg viewBox="0 0 24 24" width="26" height="26" fill="none"
                             stroke="#b79bc2" strokeWidth="1.4" aria-hidden="true">
                            <rect x="3" y="4" width="18" height="16" rx="2" />
                            <circle cx="9" cy="10" r="2" />
                            <path d="M3 17l5-4 4 3 3-2 6 5" />
                        </svg>
                        <span>{img?.label || 'image'}</span>
                    </span>
                )}
        </div>
    );
}

export default function Home() {
    const H = HOME;
    return (
        <main className="cx-page">
            <div className="home-wrap cx-fade">
                <Seo path="/" jsonLd={{
                    '@context': 'https://schema.org', '@type': 'Person',
                    name: 'Ethan Park', alternateName: ['Aris', 'Polaris'], url: SITE.url,
                    affiliation: { '@type': 'CollegeOrUniversity', name: 'Dartmouth College' },
                    knowsAbout: ['Environmental Humanities', 'Ecology', 'Music', 'Poetry'],
                }} />

                <div className="home-hero">
                    <div className="home-hero-text">
                        {H.eyebrow && <p className="home-eyebrow">{H.eyebrow}</p>}
                        <h1>{H.name}</h1>
                        {H.aka && <p className="home-aka">{H.aka}</p>}
                        {H.tagline && (
                            <div className="home-tagline">
                                {(Array.isArray(H.tagline) ? H.tagline : [H.tagline])
                                    .filter((line) => line.trim() !== '')
                                    .map((line, i) => <p key={i}>{line.trim()}</p>)}
                            </div>
                        )}
                        {H.intro?.length > 0 && (
                            <div className="home-intro">
                                {H.intro.map((p, i) => <p key={i}>{p}</p>)}
                            </div>
                        )}
                        <SocialLinks items={H.socials} />
                    </div>
                    <Frame img={H.portrait} className="home-portrait" />
                </div>

                <svg className="cx-sprig" style={{ width: 130 }} viewBox="0 0 130 24" fill="none"
                     stroke="#b79bc2" strokeWidth="1.2" strokeLinecap="round"
                     xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M10 12 H120" />
                    <path d="M46 12 c6 -7 14 -8 18 -12 c-8 1 -14 5 -18 12z" fill="#ece2f2" stroke="none" />
                    <path d="M46 12 c6 7 14 8 18 12 c-8 -1 -14 -5 -18 -12z" fill="#ece2f2" stroke="none" />
                    <path d="M84 12 c-6 -7 -14 -8 -18 -12 c8 1 14 5 18 12z" fill="#ece2f2" stroke="none" />
                    <path d="M84 12 c-6 7 -14 8 -18 12 c8 -1 14 -5 18 -12z" fill="#ece2f2" stroke="none" />
                    <circle cx="65" cy="12" r="2.2" fill="#b79bc2" stroke="none" />
                </svg>

                {H.sections?.length > 0 && (
                    <nav className="home-index" aria-label="Site sections">
                        {H.sections.map((s, i) => (
                            <Link
                                className={`hx-row ${i === H.sections.length - 1 ? 'hx-row-wide' : ''}`}
                                to={s.to}
                                key={s.to}
                            >
                                <span className="hx-num" aria-hidden="true">
                                    {String(i + 1).padStart(2, '0')}
                                </span>
                                <span className="hx-body">
                                    <span className="hx-title">
                                        {s.label}<span className="hx-arrow">&nbsp;→</span>
                                    </span>
                                    <span className="hx-desc">{s.desc}</span>
                                </span>
                            </Link>
                        ))}
                    </nav>
                )}
            </div>
        </main>
    );
}